"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import Image from "next/image";
import Link from "next/link";

export default function WaitlistPage() {
  const [formData, setFormData] = useState({
    clinicName: "",
    contactName: "",
    email: "",
    phone: "",
    message: ""
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        router.push("/");
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [success, router]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!formData.clinicName || !formData.contactName || !formData.email) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!acceptedTerms) {
      setError("You must accept the Terms of Service to join the waitlist.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to submit waitlist entry");
      }
      setSuccess(true);
      setFormData({
        clinicName: "",
        contactName: "",
        email: "",
        phone: "",
        message: ""
      });
    } catch (err: any) {
      setError(err.message || "Error submitting form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf6fa] flex flex-col items-center justify-center py-12 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-8">
        <div className="flex flex-col items-center mb-8">
          <Image src="/icons/logo-rgb.svg" alt="PetAftercare" width={180} height={40} className="mb-2" />
          <h1 className="text-3xl font-bold text-[#5b0861] mb-2 text-center">Join Our Clinic Waitlist</h1>
          <p className="text-[#002e65] text-center text-base font-medium">Fill out the form below and our team will be in touch to help you get started with PetAftercare.</p>
        </div>
        {success ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-semibold text-green-800 mb-2">Thank you!</h2>
            <p className="text-green-900 mb-4">Your clinic has been added to our waitlist. We'll be in touch soon.</p>
            <Link href="/" className="inline-block mt-2 text-[#004da9] font-semibold underline hover:text-[#0055a4] transition-colors">Back to Home</Link>
            <p className="text-xs text-gray-500 mt-2">Redirecting to home page...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="clinicName" className="block text-[#002e65] font-semibold mb-1">Clinic Name *</Label>
              <Input
                id="clinicName"
                name="clinicName"
                value={formData.clinicName}
                onChange={handleChange}
                required
                placeholder="Enter your clinic name"
                className="border border-[#002e65] text-[#002e65] rounded-lg h-12 px-4"
              />
            </div>
            <div>
              <Label htmlFor="contactName" className="block text-[#002e65] font-semibold mb-1">Contact Name *</Label>
              <Input
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                required
                placeholder="Enter your name"
                className="border border-[#002e65] text-[#002e65] rounded-lg h-12 px-4"
              />
            </div>
            <div>
              <Label htmlFor="email" className="block text-[#002e65] font-semibold mb-1">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email address"
                className="border border-[#002e65] text-[#002e65] rounded-lg h-12 px-4"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="block text-[#002e65] font-semibold mb-1">Phone</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number (optional)"
                className="border border-[#002e65] text-[#002e65] rounded-lg h-12 px-4"
              />
            </div>
            <div>
              <Label htmlFor="message" className="block text-[#002e65] font-semibold mb-1">Message</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your clinic or any specific needs... (optional)"
                className="border border-[#002e65] text-[#002e65] rounded-lg px-4 py-3 min-h-[100px]"
              />
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                className="mt-1"
              />
              <label
                htmlFor="terms"
                className="text-sm text-[#002e65] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I agree to the{" "}
                <Link href="/terms" className="text-[#004da9] hover:text-[#0055a4] underline" target="_blank">
                  Terms of Service
                </Link>
              </label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-center">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full bg-[#004da9] text-white text-lg font-semibold rounded-full h-12 hover:bg-[#0055a4] transition-colors"
              disabled={loading || !acceptedTerms}
            >
              {loading ? "Submitting..." : "Join Waitlist"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
} 